import { translate } from '@/shared/i18n/instance';
import type { OrderStatus } from '@/shared/services/api/types';

/**
 * Les commandes, groupées par ce qu'elles attendent.
 *
 * C'est le découpage du tableau de bord du vendeur (le serveur compte ces
 * mêmes groupes) : une part de l'anneau et un filtre de la liste sont la même
 * chose, vue de deux endroits. Toucher « À confirmer » sur l'anneau ouvre donc
 * la liste sur ces commandes-là, sans que l'un et l'autre puissent diverger.
 */
export type OrderGroup =
  | 'awaitingPayment'
  | 'toConfirm'
  | 'toPrepare'
  | 'inDelivery'
  | 'completed'
  | 'disputed'
  | 'cancelled';

export const ORDER_GROUPS: Record<OrderGroup, ReadonlyArray<OrderStatus>> = {
  awaitingPayment: ['AWAITING_PAYMENT'],
  toConfirm: ['PAYMENT_DECLARED'],
  toPrepare: ['PROCESSING', 'READY_FOR_DELIVERY'],
  inDelivery: ['DELIVERING', 'DELIVERED'],
  completed: ['COMPLETED'],
  disputed: ['DISPUTED'],
  cancelled: ['CANCELLED'],
};

/**
 * Les états où un litige peut s'ouvrir : le paiement a été déclaré, et la
 * commande n'est ni annulée ni déjà en litige. C'est la liste du serveur.
 */
export const DISPUTABLE_STATUSES: ReadonlyArray<OrderStatus> = [
  'PAYMENT_DECLARED',
  'PROCESSING',
  'READY_FOR_DELIVERY',
  'DELIVERING',
  'DELIVERED',
  'COMPLETED',
];

/** Les trois onglets de la liste, et les groupes que chacun rassemble. */
export type OrderScope = 'ongoing' | 'done' | 'disputed';

export const SCOPE_GROUPS: Record<OrderScope, ReadonlyArray<OrderGroup>> = {
  ongoing: ['awaitingPayment', 'toConfirm', 'toPrepare', 'inDelivery'],
  done: ['completed', 'cancelled'],
  disputed: ['disputed'],
};

const GROUP_KEYS = {
  awaitingPayment: 'orderGroup.awaitingPayment',
  toConfirm: 'orderGroup.toConfirm',
  toPrepare: 'orderGroup.toPrepare',
  inDelivery: 'orderGroup.inDelivery',
  completed: 'orderGroup.completed',
  disputed: 'orderGroup.disputed',
  cancelled: 'orderGroup.cancelled',
} as const satisfies Record<OrderGroup, string>;

/** Le nom d'un groupe : le même sur l'anneau et sur les puces de la liste. */
export function orderGroupLabel(group: OrderGroup): string {
  return translate(GROUP_KEYS[group]);
}

export function isOrderGroup(value: unknown): value is OrderGroup {
  return typeof value === 'string' && value in ORDER_GROUPS;
}

/** L'onglet qui porte ce groupe. */
export function scopeOf(group: OrderGroup): OrderScope {
  return (
    (Object.keys(SCOPE_GROUPS) as Array<OrderScope>).find((scope) =>
      SCOPE_GROUPS[scope].includes(group),
    ) ?? 'ongoing'
  );
}

/** Les états d'un onglet, ou d'un seul de ses groupes. */
export function statusesOf(scope: OrderScope, group?: OrderGroup | null): Array<OrderStatus> {
  const groups = group && SCOPE_GROUPS[scope].includes(group) ? [group] : SCOPE_GROUPS[scope];
  return groups.flatMap((key) => [...ORDER_GROUPS[key]]);
}
